/**
 * usePaidLeadOrder — live watch for the caller's own PAID-but-unconfigured
 * lead orders. Drives the Dashboard reminder (popup once per session + a
 * persistent banner) so a buyer who paid and wandered off gets steered back
 * to the Configure wizard (James 2026-07-18: in-portal reminder alongside
 * the 20-min email).
 */

import { useEffect, useState } from 'react';
import { collection, limit, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useEffectiveClientId } from './useImpersonation';

export interface PaidLeadOrder {
  orderId: string;
  amountUsd?: number;
  itemsSummary: string;
}

export function usePaidLeadOrder(): PaidLeadOrder | null {
  const effectiveClientId = useEffectiveClientId();
  const [order, setOrder] = useState<PaidLeadOrder | null>(null);

  useEffect(() => {
    if (!effectiveClientId) { setOrder(null); return; }
    const q = query(
      collection(db, 'clients', effectiveClientId, 'leadOrders'),
      where('status', '==', 'paid'),
      limit(1),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        if (snap.empty) { setOrder(null); return; }
        const d = snap.docs[0];
        const items = (d.data().items as Array<{ qty: number; pricePoint: string }>) || [];
        setOrder({
          orderId: d.id,
          amountUsd: typeof d.data().amountUsd === 'number' ? d.data().amountUsd : undefined,
          itemsSummary: items.map((it) => `${it.qty.toLocaleString('en-US')} × ${it.pricePoint}`).join(', '),
        });
      },
      (err) => {
        console.error('usePaidLeadOrder listener error:', err);
        setOrder(null);
      },
    );
    return () => unsub();
  }, [effectiveClientId]);

  return order;
}
